import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Layers, MoreVertical, Pencil, Trash2, Star } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
    AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { CreateWorkspaceDialog } from "./CreateWorkspaceDialog";
import { iconFor, accentFor } from "./workspaceMeta";
import { useTranslation } from "@/context/LanguageContext";

export const WorkspaceCard = ({ ws, index = 0, onUpdated, onDeleted }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [editOpen, setEditOpen] = useState(false);
    const Icon = iconFor(ws.icon);
    const accent = accentFor(ws.color);
    const name = ws.is_default ? t("workspace.defaultName") : ws.name;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3 }}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-surface-1 p-5 shadow-elev-1 transition-colors hover:bg-surface-2"
            data-testid={`workspace-card-${ws.id}`}
        >
            <div className="flex items-start justify-between">
                <button onClick={() => navigate(`/workspaces/${ws.id}`)} data-testid={`workspace-open-${ws.id}`} className="flex items-center gap-3 text-left">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}>
                        <Icon className="h-5 w-5" />
                    </div>
                </button>
                <div className="flex items-center gap-1">
                    {ws.is_default && <span className="flex items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-t4"><Star className="h-3 w-3" /> {t("workspace.home")}</span>}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button data-testid={`workspace-menu-${ws.id}`} className="rounded-lg p-1.5 text-t4 opacity-0 transition-opacity hover:bg-surface-3 hover:text-t1 group-hover:opacity-100"><MoreVertical className="h-4 w-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="border-border bg-surface-1">
                            <DropdownMenuItem data-testid={`workspace-edit-${ws.id}`} onClick={() => setEditOpen(true)}><Pencil className="mr-2 h-3.5 w-3.5" /> {t("workspace.card.edit")}</DropdownMenuItem>
                            {!ws.is_default && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <DropdownMenuItem data-testid={`workspace-delete-${ws.id}`} onSelect={(e) => e.preventDefault()} className="text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-3.5 w-3.5" /> {t("workspace.card.delete")}</DropdownMenuItem>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="border-border bg-surface-1">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle className="text-t1">{t("workspace.card.deleteTitle")}</AlertDialogTitle>
                                            <AlertDialogDescription className="text-t3">{t("workspace.card.deleteBody")}</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className="bg-surface-3">{t("common.cancel")}</AlertDialogCancel>
                                            <AlertDialogAction data-testid={`workspace-confirm-delete-${ws.id}`} className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90" onClick={() => onDeleted?.(ws.id)}>{t("workspace.card.deleteConfirm")}</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <button onClick={() => navigate(`/workspaces/${ws.id}`)} className="mt-4 flex-1 text-left">
                <h3 className="font-sans-display text-base font-semibold text-t1">{name}</h3>
                {ws.description && <p className="mt-1 line-clamp-2 text-sm text-t3">{ws.description}</p>}
            </button>

            <div className="mt-4 flex items-center gap-4 text-t4">
                <span className="inline-flex items-center gap-1.5 text-xs"><FileText className="h-3.5 w-3.5" /> {t("workspace.card.sources", { n: ws.source_count || 0 })}</span>
                <span className="inline-flex items-center gap-1.5 text-xs"><Layers className="h-3.5 w-3.5" /> {t("workspace.card.cards", { n: ws.flashcard_count || 0 })}</span>
            </div>

            <CreateWorkspaceDialog existing={ws} open={editOpen} onOpenChange={setEditOpen} onSaved={onUpdated} />
        </motion.div>
    );
};
